// ─────────────────────────────────────────────────────────────────────────
// Central workspace store — a tiny observable, framework-agnostic on purpose.
// React subscribes via useSyncExternalStore (see useStore), and webmcp-tools.ts
// imports `actions` directly, so an in-browser agent and the human UI drive the
// exact same code paths. Every mutation persists to localStorage.
// ─────────────────────────────────────────────────────────────────────────
import { discoverCompanyJobs, matchWithResume, searchNaukri } from "./api";
import { load, save } from "./storage";
import type {
  ActivityEntry,
  JobCardModel,
  MatchResponse,
  NaukriListing,
  PipelineStage,
} from "./types";

/** Cap on companies resolved per import — keeps LLM/rate-limit cost bounded. */
const MAX_IMPORT_COMPANIES = 6;

interface State {
  jobs: JobCardModel[];
  resume: string;
  activity: ActivityEntry[];
}

// The Resume Canvas starts empty — a resume only appears once the user uploads
// or pastes their own. No sample data ships with the app.
let state: State = {
  jobs: load<JobCardModel[]>("jobs", []),
  resume: load<string>("resume", ""),
  activity: load<ActivityEntry[]>("activity", []),
};

const listeners = new Set<() => void>();

function emit() {
  save("jobs", state.jobs);
  save("resume", state.resume);
  save("activity", state.activity.slice(0, 50)); // cap stored history
  for (const l of listeners) l();
}

function set(patch: Partial<State>) {
  state = { ...state, ...patch };
  emit();
}

function uid(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `id-${Date.now()}-${listeners.size}-${state.activity.length}`;
}

// ── Subscription API (for React) ──────────────────────────────────────────
export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
export function getState(): State {
  return state;
}

// ── Activity feed ─────────────────────────────────────────────────────────
function logStart(source: ActivityEntry["source"], tool: string, message: string): string {
  const id = uid();
  set({
    activity: [
      { id, at: Date.now(), source, tool, message, status: "running" },
      ...state.activity,
    ],
  });
  return id;
}
function logEnd(id: string, status: "ok" | "error", message?: string) {
  set({
    activity: state.activity.map((a) =>
      a.id === id ? { ...a, status, message: message ?? a.message } : a,
    ),
  });
}
function logOnce(
  source: ActivityEntry["source"],
  message: string,
  status: ActivityEntry["status"] = "ok",
  tool?: string,
) {
  set({
    activity: [
      { id: uid(), at: Date.now(), source, tool, message, status },
      ...state.activity,
    ],
  });
}

// ── Actions ───────────────────────────────────────────────────────────────
// `source` lets each action record whether the agent or the human triggered it.

export const actions = {
  clearActivity() {
    set({ activity: [] });
  },

  setResume(text: string, source: ActivityEntry["source"] = "user") {
    set({ resume: text });
    if (source === "agent") logOnce("agent", "Resume updated by agent.", "ok");
  },

  addJob(
    input: {
      title: string;
      company: string;
      officialUrl: string;
      location?: string;
      description?: string;
      department?: string;
    },
    source: ActivityEntry["source"] = "user",
  ): JobCardModel {
    const card: JobCardModel = {
      id: uid(),
      title: input.title,
      company: input.company,
      officialUrl: input.officialUrl,
      location: input.location ?? "Not specified",
      description: input.description ?? "",
      department: input.department ?? "Not specified",
      stage: "discovered",
      addedAt: Date.now(),
    };
    set({ jobs: [card, ...state.jobs] });
    logOnce(
      source,
      `Added "${card.title}" at ${card.company} to the board.`,
      "ok",
      source === "agent" ? "add_job_to_board" : undefined,
    );
    return card;
  },

  moveJob(id: string, stage: PipelineStage, source: ActivityEntry["source"] = "user") {
    const job = state.jobs.find((j) => j.id === id);
    set({ jobs: state.jobs.map((j) => (j.id === id ? { ...j, stage } : j)) });
    if (job) logOnce(source, `Moved "${job.title}" → ${stage}.`, "ok");
  },

  removeJob(id: string) {
    const job = state.jobs.find((j) => j.id === id);
    set({ jobs: state.jobs.filter((j) => j.id !== id) });
    if (job) logOnce("user", `Removed "${job.title}" from the board.`, "ok");
  },

  clearBoard() {
    const n = state.jobs.length;
    if (!n) return;
    set({ jobs: [] });
    logOnce("user", `Cleared the board (${n} role${n === 1 ? "" : "s"}).`, "ok");
  },

  /** Discover openings for a company and drop them onto the board. */
  async discover(
    args: { companyName: string; roleQuery: string },
    source: ActivityEntry["source"] = "user",
  ): Promise<{ added: number; portal: string; note: string }> {
    const logId = logStart(
      source,
      "discover_company_jobs",
      `Discovering "${args.roleQuery}" roles at ${args.companyName}…`,
    );
    try {
      // Pass the current resume so discovery filters to the user's experience.
      const res = await discoverCompanyJobs({
        ...args,
        resumeText: state.resume?.trim() || undefined,
      });
      const newCards: JobCardModel[] = res.openings.map((o) => ({
        ...o,
        id: uid(),
        company: res.companyName,
        stage: "discovered" as const,
        addedAt: Date.now(),
      }));
      set({ jobs: [...newCards, ...state.jobs] });
      logEnd(
        logId,
        "ok",
        `Found ${newCards.length} opening(s) at ${res.companyName}. ${res.note}`,
      );
      return {
        added: newCards.length,
        portal: res.careerPortalUrl,
        note: res.note,
      };
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unknown error.";
      logEnd(logId, "error", message);
      throw err;
    }
  },

  /**
   * FRONT stage of the pipeline. Take Naukri listings (passed in by an
   * in-browser agent reading the results page, or fetched server-side as a
   * fallback), harvest the unique company names, and run the OFFICIAL-portal
   * discovery for each — because Naukri gives a company name but not its portal,
   * and the company's own careers page holds the complete job list.
   */
  async importFromNaukri(
    args: { roleQuery: string; location?: string; listings?: NaukriListing[] },
    source: ActivityEntry["source"] = "user",
  ): Promise<{ companies: number; added: number; note: string }> {
    const roleQuery = args.roleQuery.trim() || "Engineer";
    const logId = logStart(
      source,
      "import_from_naukri",
      `Importing "${roleQuery}" from Naukri` +
        `${args.location ? ` (${args.location})` : ""}…`,
    );
    try {
      // Prefer listings the agent scraped from the live page; otherwise ask the
      // server to read Naukri's search API (best-effort, may be blocked).
      let listings = (args.listings ?? []).filter(
        (l) => l && l.company?.trim() && l.title?.trim(),
      );
      let sourceNote = "";
      if (!listings.length) {
        const res = await searchNaukri({ roleQuery, location: args.location });
        listings = res.listings;
        sourceNote = res.note;
      }

      // One discovery per company; dedupe case-insensitively.
      const seen = new Set<string>();
      const companies: string[] = [];
      for (const l of listings) {
        const key = l.company.trim().toLowerCase();
        if (key && !seen.has(key)) {
          seen.add(key);
          companies.push(l.company.trim());
        }
      }

      if (!companies.length) {
        logEnd(
          logId,
          "error",
          sourceNote ||
            "No companies found in the Naukri listings to import.",
        );
        return { companies: 0, added: 0, note: sourceNote };
      }

      const capped = companies.length > MAX_IMPORT_COMPANIES;
      const targets = companies.slice(0, MAX_IMPORT_COMPANIES);

      // Resolve each company's official portal in turn (sequential keeps the
      // activity feed readable and stays within free-tier LLM rate limits).
      let added = 0;
      for (const companyName of targets) {
        try {
          const res = await actions.discover({ companyName, roleQuery }, source);
          added += res.added;
        } catch {
          /* discover() already logged the failure; continue with the rest */
        }
      }

      const note =
        `Imported ${targets.length} compan${targets.length === 1 ? "y" : "ies"} ` +
        `from Naukri → ${added} official opening(s) on the board` +
        (capped
          ? `. Showed the first ${MAX_IMPORT_COMPANIES} of ${companies.length} companies.`
          : ".");
      logEnd(logId, "ok", note);
      return { companies: targets.length, added, note };
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unknown error.";
      logEnd(logId, "error", message);
      throw err;
    }
  },

  /** Score a board job against a resume and attach the result. */
  async match(
    args: { jobId: string; resumeText?: string },
    source: ActivityEntry["source"] = "user",
  ): Promise<MatchResponse> {
    const job = state.jobs.find((j) => j.id === args.jobId);
    if (!job) throw new Error(`No job on the board with id "${args.jobId}".`);
    const resumeText = args.resumeText?.trim() || state.resume;

    const logId = logStart(
      source,
      "match_with_resume",
      `Matching resume against "${job.title}" at ${job.company}…`,
    );
    try {
      const match = await matchWithResume({
        jobTitle: job.title,
        company: job.company,
        jobDescription: job.description,
        resumeText,
      });
      set({
        jobs: state.jobs.map((j) =>
          j.id === job.id ? { ...j, match } : j,
        ),
      });
      logEnd(
        logId,
        "ok",
        `Match for "${job.title}": ${match.score}% fit — ${match.summary}`,
      );
      return match;
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unknown error.";
      logEnd(logId, "error", message);
      throw err;
    }
  },

  /** Apply one suggested bullet diff into the live resume text. */
  applyDiff(jobId: string, diffIndex: number, source: ActivityEntry["source"] = "user") {
    const job = state.jobs.find((j) => j.id === jobId);
    const diff = job?.match?.bulletDiffs[diffIndex];
    if (!job || !diff) return;

    let resume = state.resume;
    if (diff.original && resume.includes(diff.original)) {
      resume = resume.replace(diff.original, diff.suggested);
    } else {
      resume = `${resume.trimEnd()}\n- ${diff.suggested}`;
    }
    // Mark the diff consumed by dropping it from the list.
    const remaining = job.match!.bulletDiffs.filter((_, i) => i !== diffIndex);
    set({
      resume,
      jobs: state.jobs.map((j) =>
        j.id === jobId ? { ...j, match: { ...j.match!, bulletDiffs: remaining } } : j,
      ),
    });
    logOnce(source, `Applied a tailored bullet from "${job.title}".`, "ok");
  },

  dismissDiff(jobId: string, diffIndex: number) {
    const job = state.jobs.find((j) => j.id === jobId);
    if (!job?.match) return;
    const remaining = job.match.bulletDiffs.filter((_, i) => i !== diffIndex);
    set({
      jobs: state.jobs.map((j) =>
        j.id === jobId ? { ...j, match: { ...j.match!, bulletDiffs: remaining } } : j,
      ),
    });
  },

  noteSystem(message: string, status: ActivityEntry["status"] = "ok") {
    logOnce("system", message, status);
  },
};

export type Actions = typeof actions;
